// One moving selection surface, independent of routing and form state.
// Only explicitly opted-in tab strips are observed; no document-wide scanning.
const controllers = new WeakMap();
const recentStrips = new Map();
const selected = '.active, [aria-selected="true"], [aria-current="page"], [aria-pressed="true"]';
export const TAB_GLIDE = { duration: 200, easing: 'cubic-bezier(.22, 1, .36, 1)' };

// Only siblings participate. More is represented by its visible summary, never
// by a hidden current link in the dropdown or another nested tab strip.
export function tabItems(element) {
  return [...element.children].flatMap(child => child.matches('a, button') ? [child]
    : child.matches('details') ? [child.querySelector(':scope > summary')].filter(Boolean) : []);
}

function tabRect(control, host) {
  let x = 0, y = 0, item = control;
  while (item && item !== host) {
    x += item.offsetLeft; y += item.offsetTop;
    item = item.offsetParent;
    if (item && item !== host) { x += item.clientLeft; y += item.clientTop; }
  }
  return { x, y, width: control.offsetWidth, height: control.offsetHeight };
}

export function tabFrames(from, to) {
  return [
    { transform: `translate(${from.x - to.x}px, ${from.y - to.y}px) scale(${from.width / to.width}, ${from.height / to.height})` },
    { transform: 'none' }
  ];
}

export function createTabMotion(element, key) {
  const indicator = document.createElement('span');
  indicator.className = 'motion-tab-indicator';
  indicator.setAttribute('aria-hidden', 'true');
  indicator.hidden = true;
  element.append(indicator);
  element.setAttribute('data-motion-tabs', '');
  const media = matchMedia('(prefers-reduced-motion: reduce)');
  const cached = key && recentStrips.get(key);
  let previous = cached && Date.now() - cached.time < 1000 ? cached.rect : null;
  let animation;
  let frame;
  let painted = false;
  let destroyed = false;

  function cancel() {
    animation?.cancel();
    animation = undefined;
  }
  function visibleRect() {
    const rect = indicator.getBoundingClientRect(), host = element.getBoundingClientRect();
    return { x: rect.left - host.left + element.scrollLeft - element.clientLeft,
      y: rect.top - host.top + element.scrollTop - element.clientTop, width: rect.width, height: rect.height };
  }
  function measure() {
    frame = undefined;
    const items = tabItems(element);
    const active = items.find(child => child.matches(selected));
    if (!active || !active.offsetWidth || !active.offsetHeight) {
      cancel(); indicator.hidden = true; previous = null;
      element.removeAttribute('data-motion-ready');
      return;
    }
    // offset geometry is unaffected by pointer-press scale and scroll position.
    const next = {
      ...tabRect(active, element),
      id: active.getAttribute('data-motion-id') || active.getAttribute('href') || active.id || String(items.indexOf(active))
    };
    const changed = previous && previous.id !== next.id;
    let from = previous;
    if (changed && animation) {
      // Continue from the visible position if the user changes direction mid-flight.
      from = visibleRect();
    }
    if (!painted || !previous || changed || ['x', 'y', 'width', 'height'].some(field => previous[field] !== next[field])) {
      cancel();
      Object.assign(indicator.style, { left: `${next.x}px`, top: `${next.y}px`, width: `${next.width}px`, height: `${next.height}px` });
      indicator.hidden = false;
      element.setAttribute('data-motion-ready', '');
      painted = true;
      if (changed && !media.matches && indicator.animate) {
        animation = indicator.animate(tabFrames(from, next), TAB_GLIDE);
        animation.id = 'nur-tab-glide';
        const current = animation;
        current.onfinish = () => { if (animation === current) cancel(); };
      }
    }
    previous = next;
  }
  function schedule() {
    if (!destroyed && frame === undefined) frame = requestAnimationFrame(measure);
  }
  const resize = new ResizeObserver(schedule);
  function observeItems() {
    resize.disconnect();
    resize.observe(element);
    for (const child of tabItems(element)) resize.observe(child);
  }
  const mutations = new MutationObserver(records => {
    if (records.every(record => record.target === indicator)) return;
    if (records.some(record => record.type === 'childList')) observeItems();
    schedule();
  });
  mutations.observe(element, { childList: true, subtree: true, attributes: true,
    attributeFilter: ['class', 'aria-current', 'aria-selected', 'aria-pressed', 'hidden', 'data-motion-id'] });
  function preferenceChange() { if (media.matches) cancel(); }
  media.addEventListener('change', preferenceChange);
  observeItems(); schedule();
  return {
    update: schedule,
    destroy() {
      destroyed = true;
      // Preserve the actual visible position during a rapid route remount, not
      // the old animation's destination. The next strip resumes from here.
      const last = animation && previous ? { ...previous, ...visibleRect() } : previous;
      cancelAnimationFrame(frame); cancel(); resize.disconnect(); mutations.disconnect();
      media.removeEventListener('change', preferenceChange);
      if (key && last) {
        // Bounded, short-lived continuity when related routes remount their nav.
        for (const [id, entry] of recentStrips) if (Date.now() - entry.time > 1000) recentStrips.delete(id);
        recentStrips.set(key, { rect: last, time: Date.now() });
      }
      indicator.remove(); element.removeAttribute('data-motion-tabs'); element.removeAttribute('data-motion-ready');
    }
  };
}

export const tabMotion = {
  mounted(element, binding) { controllers.set(element, createTabMotion(element, binding.value)); },
  updated(element) { controllers.get(element)?.update(); },
  beforeUnmount(element) { controllers.get(element)?.destroy(); controllers.delete(element); }
};
